import React, { useEffect, useState, useCallback, useMemo } from 'react'
import {
    ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import moment from 'moment';
import axios from 'axios';
import CountUp from 'react-countup';
import { adminGetDashboard, adminGetPlanAnalytics } from '../services/Api';

/* ------------------------------------------------------------------ *
 * Formatting helpers
 * ------------------------------------------------------------------ */

// ₹50,036 — Indian digit grouping (50,036 / 1,50,036)
const inr = (n) =>
    new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    }).format(n ?? 0);

// ₹50.0K for tight axis labels
const inrCompact = (n) =>
    new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: 'INR', notation: 'compact', maximumFractionDigits: 1,
    }).format(n ?? 0);

const count = (n) => new Intl.NumberFormat('en-IN').format(n ?? 0);
const pct = (n) => (n == null ? '—' : `${n}%`);

const FILTER_LABELS = {
    today: 'Today', yesterday: 'Yesterday',
    last_7_days: 'Last 7 days', last_30_days: 'Last 30 days', last_90_days: 'Last 90 days',
    this_week: 'This week', this_month: 'This month', last_month: 'Last month',
    this_year: 'This year', last_year: 'Last year', last_12_months: 'Last 12 months',
    all_time: 'All time', custom: 'Custom range…',
};

const GRANULARITY_LABELS = { hour: 'Hourly', day: 'Daily', week: 'Weekly', month: 'Monthly' };

const PALETTE = {
    subscription: '#6366f1',
    one_time: '#14b8a6',
    assigned: '#94a3b8',
    positive: '#22c55e',
    warning: '#f59e0b',
    negative: '#ef4444',
};

const API_BASE = process.env.REACT_APP_API_URL || '';

// invoice_url is a server-relative path like "./invoices/x.pdf"
const invoiceHref = (path) =>
    path ? `${API_BASE.replace(/\/$/, '')}/${String(path).replace(/^\.?\//, '')}` : null;

/* ------------------------------------------------------------------ *
 * Small shared pieces
 * ------------------------------------------------------------------ */

// change_pct is already a percent. null means the previous period was zero.
const Delta = ({ value }) => {
    if (value === null || value === undefined) {
        return <span className='text-[11px] font-semibold text-[#0F66C8] bg-blue-50 px-1.5 py-0.5 rounded'>New</span>;
    }
    if (value === 0) {
        return <span className='text-[11px] font-semibold text-gray-500'>– 0%</span>;
    }
    const up = value > 0;
    return (
        <span className={`text-[11px] font-semibold ${up ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
            {up ? '↑' : '↓'} {Math.abs(value)}%
        </span>
    );
};

const Card = ({ title, subtitle, right, children, className = '' }) => (
    <div className={`bg-white rounded-lg shadow-md p-4 ${className}`}>
        {(title || right) && (
            <div className='flex items-start justify-between gap-3 mb-3'>
                <div>
                    <p className='text-sm font-semibold text-[#394253]'>{title}</p>
                    {subtitle ? <p className='text-[11px] text-gray-400 mt-0.5'>{subtitle}</p> : null}
                </div>
                {right}
            </div>
        )}
        {children}
    </div>
);

const EmptyState = ({ title, body, action }) => (
    <div className='flex flex-col items-center justify-center text-center py-10 px-4'>
        <p className='text-sm font-semibold text-[#394253]'>{title}</p>
        {body ? <p className='text-xs text-gray-400 mt-1 max-w-md'>{body}</p> : null}
        {action ? <div className='mt-3'>{action}</div> : null}
    </div>
);

const ErrorState = ({ message, onRetry }) => (
    <div className='bg-white rounded-lg shadow-md p-8 text-center'>
        <p className='text-sm font-semibold text-[#ef4444]'>Could not load plan analytics</p>
        <p className='text-xs text-gray-500 mt-1'>{message}</p>
        <button
            type='button'
            onClick={onRetry}
            className='mt-4 px-4 py-1.5 text-xs font-medium text-white bg-[#0F66C8] rounded-md'
        >
            Retry
        </button>
    </div>
);

const Shimmer = ({ className = '' }) => (
    <div className={`bg-gray-200 rounded animate-pulse ${className}`} />
);

const AnalyticsSkeleton = () => (
    <div className='mt-6 space-y-4'>
        <Shimmer className='h-20 w-full' />
        <div className='grid grid-cols-2 lg:grid-cols-5 gap-3'>
            {[0, 1, 2, 3, 4].map((i) => <Shimmer key={i} className='h-20' />)}
        </div>
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
            <Shimmer className='h-80 lg:col-span-2' />
            <Shimmer className='h-80' />
        </div>
        <Shimmer className='h-72' />
    </div>
);

/* ------------------------------------------------------------------ *
 * Data hook — with request cancellation, so a slow earlier response
 * can never overwrite a fast later one.
 * ------------------------------------------------------------------ */

function usePlanAnalytics(filters, reloadKey) {
    const [state, setState] = useState({ data: null, loading: true, error: null });

    const needsRange = filters.filterType === 'custom';
    const rangeReady = !needsRange || Boolean(filters.from && filters.to);

    useEffect(() => {
        if (!rangeReady) {
            setState((s) => ({ ...s, loading: false }));
            return undefined;
        }
        const controller = new AbortController();
        setState((s) => ({ ...s, loading: true, error: null }));

        adminGetPlanAnalytics(filters, controller.signal)
            .then((res) => {
                const body = res?.data;
                if (!body?.success) throw new Error(body?.message || 'Request failed');
                setState({ data: body.data, loading: false, error: null });
            })
            .catch((err) => {
                // superseded by a newer request — leave the state alone
                if (axios.isCancel(err) || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
                setState((s) => ({
                    ...s,
                    loading: false,
                    error: err?.response?.data?.message || err.message,
                }));
            });

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.filterType, filters.from, filters.to, filters.granularity, filters.topLimit, rangeReady, reloadKey]);

    return state;
}

/* ------------------------------------------------------------------ *
 * Filter bar — options come from meta so the backend stays the
 * single source of truth
 * ------------------------------------------------------------------ */

const selectClass = 'h-8 px-2 text-xs text-black bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none cursor-pointer';

const FilterBar = ({ meta, filters, onChange, onRefresh, onExport, refreshing }) => {
    const available = meta?.available_filters?.length
        ? meta.available_filters
        : Object.keys(FILTER_LABELS);
    const granularities = meta?.available_granularities?.length
        ? meta.available_granularities
        : Object.keys(GRANULARITY_LABELS);

    // range.to is exclusive — subtract a day so the label reads the way a human expects
    const rangeLabel = meta?.range
        ? `${moment(meta.range.from).format('DD MMM')} – ${moment(meta.range.to).subtract(1, 'day').format('DD MMM YYYY')}`
        : '—';

    return (
        <div className='bg-white rounded-lg shadow-md p-4'>
            <div className='block lg:flex lg:items-start lg:justify-between gap-4'>
                <div>
                    <p className='text-lg font-semibold text-[#394253]'>Plan Revenue Analytics</p>
                    <p className='text-xs text-gray-400 mt-0.5'>
                        {rangeLabel}
                        {filters.filterType !== 'all_time' ? ' · vs previous period' : ''}
                    </p>
                </div>
                <div className='flex flex-wrap items-center gap-2 mt-3 lg:mt-0'>
                    <select
                        className={selectClass}
                        value={filters.filterType}
                        aria-label='Date range'
                        onChange={(e) => onChange({ ...filters, filterType: e.target.value })}
                    >
                        {available.map((f) => (
                            <option key={f} value={f}>{FILTER_LABELS[f] || f}</option>
                        ))}
                    </select>

                    {filters.filterType === 'custom' && (
                        <>
                            <input
                                type='date'
                                className={selectClass}
                                value={filters.from || ''}
                                aria-label='From date'
                                onChange={(e) => onChange({ ...filters, from: e.target.value })}
                            />
                            <input
                                type='date'
                                className={selectClass}
                                value={filters.to || ''}
                                aria-label='To date'
                                onChange={(e) => onChange({ ...filters, to: e.target.value })}
                            />
                        </>
                    )}

                    <select
                        className={selectClass}
                        value={filters.granularity || ''}
                        aria-label='Bucket size'
                        onChange={(e) => onChange({ ...filters, granularity: e.target.value || undefined })}
                    >
                        <option value=''>Auto{meta?.granularity ? ` (${GRANULARITY_LABELS[meta.granularity] || meta.granularity})` : ''}</option>
                        {granularities.map((g) => (
                            <option key={g} value={g}>{GRANULARITY_LABELS[g] || g}</option>
                        ))}
                    </select>

                    <select
                        className={selectClass}
                        value={filters.topLimit}
                        aria-label='Rows in top lists'
                        onChange={(e) => onChange({ ...filters, topLimit: Number(e.target.value) })}
                    >
                        {[10, 25, 50].map((n) => <option key={n} value={n}>Top {n}</option>)}
                    </select>

                    <button
                        type='button'
                        onClick={onRefresh}
                        disabled={refreshing}
                        title='Refresh'
                        className='h-8 px-3 text-xs font-medium text-[#0F66C8] bg-white border border-gray-300 rounded-md disabled:opacity-50'
                    >
                        ⟳ Refresh
                    </button>
                    <button
                        type='button'
                        onClick={onExport}
                        className='h-8 px-3 text-xs font-medium text-white bg-[#0F66C8] rounded-md'
                    >
                        Export CSV
                    </button>
                </div>
            </div>
            {filters.filterType === 'custom' && (!filters.from || !filters.to) && (
                <p className='text-[11px] text-[#f59e0b] mt-2'>Pick both a start and an end date to load the custom range.</p>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ *
 * Alert strip — only rendered when something is actually non-zero
 * ------------------------------------------------------------------ */

const AlertStrip = ({ membership, health }) => {
    const alerts = [];
    if (membership?.expiring_next_7_days > 0) {
        alerts.push(`${count(membership.expiring_next_7_days)} membership${membership.expiring_next_7_days === 1 ? '' : 's'} expiring in 7 days`);
    }
    if (health?.in_grace > 0) {
        alerts.push(`${count(health.in_grace)} payment${health.in_grace === 1 ? '' : 's'} in grace`);
    }
    if (health?.needs_attention > 0) {
        alerts.push(`${count(health.needs_attention)} mandate${health.needs_attention === 1 ? '' : 's'} need attention`);
    }
    if (!alerts.length) return null;

    return (
        <div className='flex items-center gap-2 bg-amber-50 border border-amber-200 text-[#92400e] rounded-lg px-4 py-2.5 text-xs font-medium'>
            <span aria-hidden='true'>⚠</span>
            <span>{alerts.join('  ·  ')}</span>
        </div>
    );
};

/* ------------------------------------------------------------------ *
 * KPI row
 * ------------------------------------------------------------------ */

const KpiRow = ({ summary, comparison }) => {
    const c = comparison?.change_pct ?? {};
    const cards = [
        { label: 'Revenue', value: inr(summary.total_revenue), delta: c.total_revenue },
        { label: 'Transactions', value: count(summary.total_transactions), delta: c.total_transactions },
        { label: 'Paying users', value: count(summary.paying_users), delta: c.paying_users },
        { label: 'Avg order', value: inr(summary.average_order_value), delta: c.average_order_value },
        // live snapshot — explicitly labelled so nobody reads MRR as period revenue
        { label: 'MRR', value: inr(summary.mrr), hint: 'current' },
    ];
    return (
        <div className='grid grid-cols-2 lg:grid-cols-5 gap-3'>
            {cards.map((k) => (
                <div key={k.label} className='bg-white rounded-lg shadow-md px-4 py-3'>
                    <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wide'>{k.label}</p>
                    <p className='text-xl font-semibold text-[#394253] mt-1 tabular-nums'>{k.value}</p>
                    <div className='mt-1'>
                        {k.hint
                            ? <span className='text-[11px] text-gray-400'>{k.hint}</span>
                            : (comparison ? <Delta value={k.delta} /> : <span className='text-[11px] text-gray-400'>no comparison</span>)}
                    </div>
                </div>
            ))}
        </div>
    );
};

/* ------------------------------------------------------------------ *
 * Revenue trend — stacked area, with a Revenue / Transactions toggle
 * ------------------------------------------------------------------ */

// The pre-formatted `label` is built for a tooltip, not an axis — a week reads
// "17 Aug - 23 Aug". Trim it to the shortest form that still identifies the
// bucket; the tooltip keeps the full label.
const shortTick = (label, granularity) => {
    if (!label) return '';
    switch (granularity) {
        case 'hour': return label.split(', ')[1] || label;          // 21 Aug, 14:00 -> 14:00
        case 'day': return label.split(' ').slice(0, 2).join(' ');  // 21 Aug 2026   -> 21 Aug
        case 'week': return label.split(' - ')[0];                  // 17 - 23 Aug   -> 17 Aug
        case 'month': return label.replace(/ \d{2}(\d{2})$/, " '$1"); // Aug 2026    -> Aug '26
        default: return label;
    }
};

const RevenueTrend = ({ trend, summary, granularity }) => {
    const [metric, setMetric] = useState('revenue');
    const isRevenue = metric === 'revenue';
    const fmt = isRevenue ? inr : count;
    const tick = useCallback((l) => shortTick(l, granularity), [granularity]);
    // angled + shortened labels fit roughly 16 across the card without touching
    const step = Math.max(1, Math.ceil(trend.length / 16));

    return (
        <Card
            title='Revenue over time'
            subtitle={`${trend.length} ${granularity} buckets · subscription stacked over one-time`}
            right={
                <div className='flex rounded-md border border-gray-300 overflow-hidden text-[11px] shrink-0'>
                    {[['revenue', 'Revenue'], ['transactions', 'Transactions']].map(([key, label]) => (
                        <button
                            key={key}
                            type='button'
                            onClick={() => setMetric(key)}
                            className={`px-2.5 py-1 font-medium ${metric === key ? 'bg-[#0F66C8] text-white' : 'bg-white text-gray-500'}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            }
        >
            <div
                role='img'
                aria-label={`${isRevenue ? `Revenue ${inr(summary.total_revenue)}` : `${count(summary.total_transactions)} transactions`} over ${trend.length} ${granularity} buckets`}
                style={{ width: '100%', height: 340 }}
            >
                <ResponsiveContainer>
                    <AreaChart data={trend} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                        <defs>
                            <linearGradient id='gSub' x1='0' y1='0' x2='0' y2='1'>
                                <stop offset='0%' stopColor={PALETTE.subscription} stopOpacity={0.85} />
                                <stop offset='100%' stopColor={PALETTE.subscription} stopOpacity={0.15} />
                            </linearGradient>
                            <linearGradient id='gOne' x1='0' y1='0' x2='0' y2='1'>
                                <stop offset='0%' stopColor={PALETTE.one_time} stopOpacity={0.85} />
                                <stop offset='100%' stopColor={PALETTE.one_time} stopOpacity={0.15} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#e5e7eb' />
                        <XAxis dataKey='label' interval={step - 1} tickLine={false}
                            tickFormatter={tick} tickMargin={8}
                            angle={-45} textAnchor='end' height={58}
                            tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <YAxis tickFormatter={isRevenue ? inrCompact : count} tickLine={false} axisLine={false}
                            allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} width={isRevenue ? 70 : 45} />
                        <Tooltip
                            formatter={(v, name) => [fmt(v), name]}
                            labelFormatter={(l) => l}
                            contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                        />
                        <Legend iconType='circle' wrapperStyle={{ fontSize: 12 }} />
                        <Area type='monotone' dataKey={isRevenue ? 'one_time_revenue' : 'one_time_transactions'} name='One-time'
                            stackId='rev' stroke={PALETTE.one_time} fill='url(#gOne)' strokeWidth={2} />
                        <Area type='monotone' dataKey={isRevenue ? 'subscription_revenue' : 'subscription_transactions'} name='Subscription'
                            stackId='rev' stroke={PALETTE.subscription} fill='url(#gSub)' strokeWidth={2} />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </Card>
    );
};

/* ------------------------------------------------------------------ *
 * Revenue mix — donut. The `assigned` slice is always ₹0, so it is
 * dropped from the pie and reported as a give-away count instead.
 * ------------------------------------------------------------------ */

const RevenueMix = ({ split, total, summary }) => {
    const slices = (split || []).filter((s) => s.revenue > 0);

    return (
        <Card title='Revenue mix' subtitle='Subscription vs one-time'>
            {!slices.length ? (
                <EmptyState title='No revenue in this period' />
            ) : (
                <div className='relative'>
                    <div
                        role='img'
                        aria-label={slices.map((s) => `${s.label} ${inr(s.revenue)}`).join(', ')}
                        style={{ width: '100%', height: 260 }}
                    >
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie data={slices} dataKey='revenue' nameKey='label'
                                    innerRadius={70} outerRadius={100} paddingAngle={2} strokeWidth={0}>
                                    {slices.map((s) => <Cell key={s.key} fill={PALETTE[s.key] || PALETTE.assigned} />)}
                                </Pie>
                                <Tooltip
                                    formatter={(v, n) => [`${inr(v)} (${total ? Math.round((v / total) * 100) : 0}%)`, n]}
                                    contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                                />
                                <Legend iconType='circle' wrapperStyle={{ fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    {/* centre label */}
                    <div className='absolute left-1/2 top-[45%] -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none'>
                        <p className='text-[11px] text-gray-400'>Total</p>
                        <p className='text-lg font-semibold text-[#394253] tabular-nums'>{inr(total)}</p>
                    </div>
                </div>
            )}
            <div className='mt-2 pt-3 border-t text-[11px] text-gray-500 space-y-1'>
                <p>Subscription share: <span className='font-semibold text-[#394253]'>{pct(summary.subscription_revenue_share_pct)}</span></p>
                <p>
                    Admin-assigned (free): <span className='font-semibold text-[#394253]'>{count(summary.assigned_plans)}</span>
                    {' '}plan{summary.assigned_plans === 1 ? '' : 's'} · notional {inr(summary.assigned_plans_notional_value)}
                </p>
                <p className='text-gray-400'>Assigned plans are give-aways — no money changed hands, so they are not part of revenue.</p>
            </div>
        </Card>
    );
};

/* ------------------------------------------------------------------ *
 * Plan popularity — horizontal, because plan names are long and
 * would be unreadable rotated
 * ------------------------------------------------------------------ */

const PlanPopularity = ({ plans }) => {
    const data = (plans || []).filter((p) => p.purchases > 0).slice(0, 8);
    return (
        <Card title='Plan popularity' subtitle='Most → least purchased'>
            {!data.length ? (
                <EmptyState title='No plans sold in this period' />
            ) : (
                <div
                    role='img'
                    aria-label={data.map((p) => `${p.plan_name}: ${count(p.purchases)} purchases`).join(', ')}
                    style={{ width: '100%', height: Math.max(220, data.length * 44) }}
                >
                    <ResponsiveContainer>
                        <BarChart data={data} layout='vertical' margin={{ left: 8, right: 32 }}>
                            <XAxis type='number' hide />
                            <YAxis type='category' dataKey='plan_name' width={130}
                                tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                            <Tooltip
                                formatter={(v, n, { payload }) =>
                                    [`${count(v)} purchases · ${inr(payload.revenue)}`, payload.plan_name]}
                                contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                            />
                            <Bar dataKey='purchases' radius={[0, 6, 6, 0]} barSize={22}>
                                {data.map((p, i) => (
                                    <Cell key={p.plan_id || p.plan_name} fill={i === 0 ? PALETTE.subscription : '#c7d2fe'} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </Card>
    );
};

/* ------------------------------------------------------------------ *
 * Plan highlights — most / least / highest revenue + never sold
 * ------------------------------------------------------------------ */

const PopularityCallouts = ({ popularity }) => {
    const items = [
        ['Most popular', popularity?.most_popular, (p) => `${count(p.purchases)} purchases`],
        ['Highest revenue', popularity?.highest_revenue, (p) => inr(p.revenue)],
        ['Least popular', popularity?.least_popular, (p) => `${count(p.purchases)} purchases`],
    ];
    return (
        <Card title='Plan highlights'>
            <div className='space-y-2.5'>
                {items.map(([label, plan, fmt]) => (
                    <div key={label} className='flex items-start justify-between gap-3 text-xs'>
                        <span className='text-gray-400'>{label}</span>
                        {plan
                            ? (
                                <span className='font-semibold text-[#394253] text-right'>
                                    {plan.plan_name}
                                    <span className='block text-[11px] font-normal text-gray-400 tabular-nums'>{fmt(plan)}</span>
                                </span>
                            )
                            : <span className='text-gray-400'>—</span>}
                    </div>
                ))}
            </div>
            {popularity?.never_sold_in_range?.length > 0 && (
                <div className='mt-3 pt-3 border-t'>
                    <p className='text-[11px] font-semibold text-[#f59e0b]'>
                        Not selling ({popularity.never_sold_in_range.length})
                    </p>
                    <div className='flex flex-wrap gap-1.5 mt-1.5'>
                        {popularity.never_sold_in_range.map((p) => (
                            <span key={p.plan_id || p.plan_name} className='text-[11px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded'>
                                {p.plan_name} · {inr(p.price)}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </Card>
    );
};

/* ------------------------------------------------------------------ *
 * Subscription funnel — live snapshot, ignores the date filter
 * ------------------------------------------------------------------ */

const SubscriptionFunnel = ({ health }) => {
    const stages = [
        { label: 'Pending auth', value: health.pending_authorization, color: '#94a3b8' },
        { label: 'Active', value: health.active, color: PALETTE.positive },
        { label: 'In grace', value: health.in_grace, color: PALETTE.warning },
        { label: 'Paused', value: health.paused, color: '#a78bfa' },
        { label: 'Cancelled', value: health.cancelled, color: PALETTE.negative },
        { label: 'Expired', value: health.expired, color: '#6b7280' },
    ];
    return (
        <Card title='Subscription funnel' subtitle='Current mandates — not affected by the date filter'>
            <div
                role='img'
                aria-label={stages.map((s) => `${s.label}: ${count(s.value)}`).join(', ')}
                style={{ width: '100%', height: 240 }}
            >
                <ResponsiveContainer>
                    <BarChart data={stages} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <XAxis dataKey='label' tick={{ fontSize: 10 }} tickLine={false} interval={0} />
                        <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11 }} width={30} />
                        <Tooltip formatter={(v) => [count(v), 'Mandates']}
                            contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }} />
                        <Bar dataKey='value' radius={[6, 6, 0, 0]} barSize={30}>
                            {stages.map((s) => <Cell key={s.label} fill={s.color} />)}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <dl className='grid grid-cols-2 gap-x-4 gap-y-1.5 mt-3 pt-3 border-t text-[11px]'>
                {[
                    ['Total mandates', count(health.total_mandates)],
                    ['ARR (current)', inr(health.arr)],
                    ['Created in range', count(health.created_in_range)],
                    ['Activated in range', count(health.activated_in_range)],
                    ['Churned in range', count(health.churned_in_range)],
                    ['Churn rate', pct(health.churn_rate_pct)],
                    ['Cycles charged', count(health.total_cycles_charged)],
                    ['Failed attempts', count(health.total_failed_attempts)],
                    ['Auto-renew on', count(health.auto_renew_on)],
                    ['Next 30 days', `${count(health.upcoming_30_days?.count)} · ${inr(health.upcoming_30_days?.expected_revenue)}`],
                ].map(([k, v]) => (
                    <div key={k} className='flex justify-between gap-2'>
                        <dt className='text-gray-400'>{k}</dt>
                        <dd className='font-semibold text-[#394253] tabular-nums'>{v}</dd>
                    </div>
                ))}
            </dl>
            {(health.in_grace > 0 || health.needs_attention > 0) && (
                <p className='text-[11px] font-semibold text-[#ef4444] mt-2'>
                    ⚠ {count(health.in_grace)} in grace · {count(health.needs_attention)} need attention — money at risk.
                </p>
            )}
        </Card>
    );
};

/* ------------------------------------------------------------------ *
 * New vs renewal charges
 * ------------------------------------------------------------------ */

const CycleSplit = ({ cycleSplit, summary }) => {
    const data = cycleSplit || [];
    // healthy subscriptions grow renewals faster than first charges
    const stalling = summary.new_subscription_charges > 0 && summary.renewal_charges === 0;
    return (
        <Card title='New vs renewal charges' subtitle='Subscription cycle split'>
            {!data.length ? (
                <EmptyState title='No subscription charges in this period' />
            ) : (
                <div
                    role='img'
                    aria-label={data.map((d) => `${d.label}: ${count(d.transactions)}`).join(', ')}
                    style={{ width: '100%', height: 240 }}
                >
                    <ResponsiveContainer>
                        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#e5e7eb' />
                            <XAxis dataKey='label' tick={{ fontSize: 11 }} tickLine={false} interval={0} />
                            <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11 }} width={30} />
                            <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }} />
                            <Bar dataKey='transactions' name='Charges' fill={PALETTE.subscription} radius={[6, 6, 0, 0]} barSize={40} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
            <div className='mt-3 pt-3 border-t text-[11px] text-gray-500 space-y-1'>
                <p>New mandate charges: <span className='font-semibold text-[#394253]'>{count(summary.new_subscription_charges)}</span></p>
                <p>Renewals: <span className='font-semibold text-[#394253]'>{count(summary.renewal_charges)}</span> · renewal share {pct(summary.renewal_share_of_subscription_pct)}</p>
                <p>Repeat buyers: <span className='font-semibold text-[#394253]'>{count(summary.repeat_buyers)}</span> · single-purchase {count(summary.single_purchase_buyers)}</p>
                {stalling && (
                    <p className='font-semibold text-[#f59e0b]'>
                        ⚠ Mandates are being authorized but not renewing — worth investigating.
                    </p>
                )}
            </div>
        </Card>
    );
};

/* ------------------------------------------------------------------ *
 * Membership — current state of the user base
 * ------------------------------------------------------------------ */

const MembershipCard = ({ membership }) => (
    <Card title='Membership' subtitle={`Current snapshot · scope: ${membership.scope}`}>
        <div className='flex items-baseline gap-2'>
            <p className='text-2xl font-semibold text-[#394253] tabular-nums'>{pct(membership.conversion_rate_pct)}</p>
            <p className='text-[11px] text-gray-400'>of {count(membership.total_users)} users have converted</p>
        </div>
        <div className='w-full h-2 bg-gray-100 rounded-full overflow-hidden mt-2'>
            <div className='h-full bg-[#22c55e]' style={{ width: `${Math.min(100, membership.conversion_rate_pct || 0)}%` }} />
        </div>
        <dl className='grid grid-cols-2 gap-x-4 gap-y-1.5 mt-4 pt-3 border-t text-[11px]'>
            {[
                ['Active members', count(membership.active_members)],
                ['Purchased', count(membership.active_purchased)],
                ['Assigned', count(membership.active_assigned)],
                ['Lapsed', count(membership.lapsed_members)],
                ['Never subscribed', count(membership.never_subscribed)],
                ['Expiring ≤ 7 days', count(membership.expiring_next_7_days)],
                ['Expiring ≤ 30 days', count(membership.expiring_next_30_days)],
            ].map(([k, v]) => (
                <div key={k} className='flex justify-between gap-2'>
                    <dt className='text-gray-400'>{k}</dt>
                    <dd className='font-semibold text-[#394253] tabular-nums'>{v}</dd>
                </div>
            ))}
        </dl>
        {membership.expiring_next_30_days > 0 && (
            <p className='text-[11px] font-semibold text-[#f59e0b] mt-2'>
                ⚠ {count(membership.expiring_next_30_days)} expiring in 30 days — retention target.
            </p>
        )}
    </Card>
);

/* ------------------------------------------------------------------ *
 * Tables
 * ------------------------------------------------------------------ */

const th = 'text-left text-[#394253] py-2.5 px-3 text-[11px] font-semibold whitespace-nowrap';
const thNum = 'text-right text-[#394253] py-2.5 px-3 text-[11px] font-semibold whitespace-nowrap';
const td = 'py-2.5 px-3 text-xs text-[#394253]';
const tdNum = 'py-2.5 px-3 text-xs text-[#394253] text-right tabular-nums whitespace-nowrap';

const PlansTable = ({ plans }) => (
    <Card title='Plans' subtitle='Greyed rows are catalogue plans that sold nothing in this window'>
        <div className='border rounded-lg overflow-x-auto'>
            <table className='w-full'>
                <caption className='sr-only'>Plan performance for the selected period</caption>
                <thead>
                    <tr className='bg-[#E0F1F6]'>
                        <th scope='col' className={th}>#</th>
                        <th scope='col' className={th}>Plan</th>
                        <th scope='col' className={thNum}>Revenue</th>
                        <th scope='col' className={thNum}>Purchases</th>
                        <th scope='col' className={thNum}>Sub / One-time</th>
                        <th scope='col' className={thNum}>Renewals</th>
                        <th scope='col' className={thNum}>Buyers</th>
                        <th scope='col' className={thNum}>Active now</th>
                        <th scope='col' className={thNum}>Assigned</th>
                        <th scope='col' className={thNum}>Last sold</th>
                        <th scope='col' className={thNum}>Share</th>
                    </tr>
                </thead>
                <tbody>
                    {plans.map((p) => (
                        <tr key={p.plan_id || p.plan_name} className={`border-b last:border-0 ${p.purchases === 0 ? 'opacity-50' : ''}`}>
                            <td className={td}>{p.popularity_rank ?? '—'}</td>
                            <td className={td}>
                                <div className='flex items-center gap-1.5 flex-wrap'>
                                    <span className='font-medium'>{p.plan_name}</span>
                                    {p.recommended && <span className='text-[10px] font-semibold text-[#0F66C8] bg-blue-50 px-1.5 py-0.5 rounded'>Recommended</span>}
                                    {p.is_deleted && <span className='text-[10px] font-semibold text-[#ef4444] bg-red-50 px-1.5 py-0.5 rounded'>Deleted</span>}
                                </div>
                                <p className='text-[11px] text-gray-400 mt-0.5'>
                                    {p.price != null ? `${inr(p.price)} · ${p.validity} ${p.validity_type}` : '—'}
                                </p>
                            </td>
                            <td className={tdNum}>{inr(p.revenue)}</td>
                            <td className={tdNum}>{count(p.purchases)}</td>
                            <td className={tdNum}>{count(p.subscription_purchases)} / {count(p.one_time_purchases)}</td>
                            <td className={tdNum}>{count(p.renewals)}</td>
                            <td className={tdNum}>{count(p.unique_buyers)}</td>
                            <td className={tdNum}>{count(p.active_members)}</td>
                            <td className={tdNum}>
                                {count(p.assigned_count)}
                                {p.assigned_value > 0 ? <span className='text-gray-400'> ({inr(p.assigned_value)})</span> : null}
                            </td>
                            <td className={tdNum}>{p.last_sold_at ? moment(p.last_sold_at).format('DD MMM YY') : '—'}</td>
                            <td className={tdNum}>
                                <div className='flex items-center justify-end gap-2'>
                                    <span className='inline-block w-14 h-1.5 bg-gray-100 rounded-full overflow-hidden'>
                                        <span className='block h-full bg-[#6366f1]' style={{ width: `${Math.min(100, p.revenue_share_pct || 0)}%` }} />
                                    </span>
                                    {pct(p.revenue_share_pct)}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </Card>
);

const TopCustomers = ({ rows }) => (
    <Card title='Top customers' subtitle='By spend in this period'>
        {!rows?.length ? (
            <EmptyState title='No paying customers in this period' />
        ) : (
            <div className='border rounded-lg overflow-x-auto'>
                <table className='w-full'>
                    <caption className='sr-only'>Highest-spending customers for the selected period</caption>
                    <thead>
                        <tr className='bg-[#E0F1F6]'>
                            <th scope='col' className={th}>Customer</th>
                            <th scope='col' className={th}>City</th>
                            <th scope='col' className={thNum}>Spent</th>
                            <th scope='col' className={thNum}>Txns</th>
                            <th scope='col' className={th}>Last plan</th>
                            <th scope='col' className={thNum}>Membership</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((c) => (
                            <tr key={c.user_id} className='border-b last:border-0'>
                                <td className={td}>
                                    {/* name can be null — fall back to the phone number */}
                                    <span className='font-medium'>{c.name || c.phoneNumber || '—'}</span>
                                    <span className='block text-[11px] text-gray-400'>{c.phoneNumber || c.email || c.unique_id || ''}</span>
                                </td>
                                <td className={td}>{c.city || '—'}</td>
                                <td className={tdNum}>{inr(c.total_spent)}</td>
                                <td className={tdNum}>
                                    {count(c.transactions)}
                                    {c.subscription_transactions > 0 ? <span className='text-gray-400'> ({count(c.subscription_transactions)} sub)</span> : null}
                                </td>
                                <td className={td}>
                                    {c.last_plan_name || '—'}
                                    <span className='block text-[11px] text-gray-400'>
                                        {c.last_purchase_at ? moment(c.last_purchase_at).format('DD MMM YY') : ''}
                                    </span>
                                </td>
                                <td className={tdNum}>
                                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${c.is_membership_active ? 'text-[#166534] bg-green-50' : 'text-gray-500 bg-gray-100'}`}>
                                        {c.is_membership_active ? 'Active' : 'Lapsed'}
                                    </span>
                                    <span className='block text-[11px] text-gray-400 mt-0.5'>
                                        {c.plan_end_date ? moment(c.plan_end_date).format('DD MMM YY') : ''}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </Card>
);

/* ------------------------------------------------------------------ *
 * Revenue by city / by source
 * ------------------------------------------------------------------ */

const RevenueByCity = ({ cities }) => {
    const data = (cities || []).slice(0, 10);
    return (
        <Card title='Revenue by city' subtitle='Top cities · "Unknown" = no city on profile'>
            {!data.length ? (
                <EmptyState title='No city revenue in this period' />
            ) : (
                <div
                    role='img'
                    aria-label={data.map((c) => `${c.city}: ${inr(c.revenue)}`).join(', ')}
                    style={{ width: '100%', height: Math.max(220, data.length * 34) }}
                >
                    <ResponsiveContainer>
                        <BarChart data={data} layout='vertical' margin={{ left: 8, right: 24 }}>
                            <XAxis type='number' hide />
                            <YAxis type='category' dataKey='city' width={100}
                                tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                            <Tooltip
                                formatter={(v, n, { payload }) =>
                                    [`${inr(v)} · ${count(payload.transactions)} txns · ${count(payload.customers)} customers`, payload.city]}
                                contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                            />
                            <Bar dataKey='revenue' fill={PALETTE.one_time} radius={[0, 6, 6, 0]} barSize={16} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </Card>
    );
};

const RevenueBySource = ({ sources }) => {
    const data = sources || [];
    const totalTxns = data.reduce((s, r) => s + (r.transactions || 0), 0);
    const reconcile = data.find((s) => s.source === 'reconcile');
    const reconcileShare = reconcile && totalTxns ? (reconcile.transactions / totalTxns) * 100 : 0;
    return (
        <Card title='Revenue by source' subtitle='Where each ledger row came from'>
            {!data.length ? (
                <EmptyState title='No transactions in this period' />
            ) : (
                <table className='w-full'>
                    <caption className='sr-only'>Revenue grouped by ledger source</caption>
                    <thead>
                        <tr className='border-b'>
                            <th scope='col' className={th}>Source</th>
                            <th scope='col' className={thNum}>Revenue</th>
                            <th scope='col' className={thNum}>Txns</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((s) => (
                            <tr key={s.source} className='border-b last:border-0'>
                                <td className={td}>{s.source}</td>
                                <td className={tdNum}>{inr(s.revenue)}</td>
                                <td className={tdNum}>{count(s.transactions)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {reconcileShare > 25 && (
                <p className='text-[11px] font-semibold text-[#f59e0b] mt-2'>
                    ⚠ {Math.round(reconcileShare)}% of rows came from the nightly reconcile — the webhook may be failing.
                </p>
            )}
        </Card>
    );
};

/* ------------------------------------------------------------------ *
 * Recent transactions — paginated client-side
 * ------------------------------------------------------------------ */

const TYPE_BADGE = {
    subscription: 'text-[#3730a3] bg-indigo-50',
    one_time: 'text-[#115e59] bg-teal-50',
    assigned: 'text-gray-600 bg-gray-100',
};

const TYPE_LABEL = { subscription: 'Subscription', one_time: 'One-time', assigned: 'Assigned' };

const RecentTransactions = ({ rows }) => {
    const PAGE_SIZE = 10;
    const [page, setPage] = useState(1);
    const all = rows || [];
    const pages = Math.max(1, Math.ceil(all.length / PAGE_SIZE));
    const current = Math.min(page, pages);
    const slice = all.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

    return (
        <Card
            title='Recent transactions'
            subtitle={`${count(all.length)} most recent`}
            right={pages > 1 ? (
                <div className='flex items-center gap-2 text-[11px] shrink-0'>
                    <button type='button' disabled={current === 1} onClick={() => setPage(current - 1)}
                        className='px-2 py-1 border border-gray-300 rounded disabled:opacity-40'>Prev</button>
                    <span className='text-gray-400'>{current} / {pages}</span>
                    <button type='button' disabled={current === pages} onClick={() => setPage(current + 1)}
                        className='px-2 py-1 border border-gray-300 rounded disabled:opacity-40'>Next</button>
                </div>
            ) : null}
        >
            {!all.length ? (
                <EmptyState title='No transactions in this period' />
            ) : (
                <div className='border rounded-lg overflow-x-auto'>
                    <table className='w-full'>
                        <caption className='sr-only'>Most recent plan transactions</caption>
                        <thead>
                            <tr className='bg-[#E0F1F6]'>
                                <th scope='col' className={th}>Date</th>
                                <th scope='col' className={th}>Customer</th>
                                <th scope='col' className={th}>Plan</th>
                                <th scope='col' className={thNum}>Amount</th>
                                <th scope='col' className={th}>Type</th>
                                <th scope='col' className={thNum}>Cycle</th>
                                <th scope='col' className={th}>Source</th>
                                <th scope='col' className={th}>Validity</th>
                                <th scope='col' className={th}>Invoice</th>
                            </tr>
                        </thead>
                        <tbody>
                            {slice.map((t) => (
                                <tr key={t._id} className='border-b last:border-0'>
                                    {/* transaction_date is always present; createdAt is null on legacy rows */}
                                    <td className={td}>{t.transaction_date ? moment(t.transaction_date).format('DD MMM YY, HH:mm') : '—'}</td>
                                    <td className={td}>
                                        <span className='font-medium'>{t.name || t.phoneNumber || '—'}</span>
                                        <span className='block text-[11px] text-gray-400'>{t.phoneNumber || ''}</span>
                                    </td>
                                    <td className={td}>{t.plan_name || '—'}</td>
                                    <td className={tdNum}>
                                        {inr(t.amount)}
                                        {t.revenue_type === 'assigned' && t.listed_price > 0
                                            ? <span className='block text-[11px] text-gray-400'>listed {inr(t.listed_price)}</span>
                                            : null}
                                    </td>
                                    <td className={td}>
                                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${TYPE_BADGE[t.revenue_type] || TYPE_BADGE.assigned}`}>
                                            {TYPE_LABEL[t.revenue_type] || t.revenue_type}
                                        </span>
                                    </td>
                                    <td className={tdNum}>
                                        {t.cycle == null ? '—' : t.cycle === 1 ? '1 (new)' : `${t.cycle} (renewal)`}
                                    </td>
                                    <td className={td}>{t.source || '—'}</td>
                                    <td className={td}>
                                        {t.start_date ? moment(t.start_date).format('DD MMM YY') : '—'}
                                        {' → '}
                                        {t.end_date ? moment(t.end_date).format('DD MMM YY') : '—'}
                                    </td>
                                    <td className={td}>
                                        {t.invoice_url
                                            ? <a href={invoiceHref(t.invoice_url)} target='_blank' rel='noreferrer' className='text-[#0F66C8] underline'>PDF</a>
                                            : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
};

/* ------------------------------------------------------------------ *
 * Client-side CSV export — everything needed is already in the response
 * ------------------------------------------------------------------ */

function exportPlansCsv(data) {
    const cols = ['popularity_rank', 'plan_name', 'price', 'validity', 'validity_type',
        'revenue', 'purchases', 'subscription_purchases', 'one_time_purchases',
        'renewals', 'unique_buyers', 'active_members', 'revenue_share_pct'];
    const esc = (v) => {
        const s = v == null ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [cols.join(','), ...data.plan_breakdown.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');

    // the BOM makes Excel read ₹ and UTF-8 names correctly
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
    const a = Object.assign(document.createElement('a'), {
        href: url,
        download: `plan-analytics-${data.meta.filterType}-${data.meta.range.from.slice(0, 10)}.csv`,
    });
    a.click();
    URL.revokeObjectURL(url);
}

/* ------------------------------------------------------------------ *
 * Plan & revenue analytics section
 * ------------------------------------------------------------------ */

const PlanAnalytics = () => {
    const [filters, setFilters] = useState({ filterType: 'last_30_days', topLimit: 10 });
    const [reloadKey, setReloadKey] = useState(0);
    const { data, loading, error } = usePlanAnalytics(filters, reloadKey);

    if (loading && !data) return <AnalyticsSkeleton />;
    if (error && !data) {
        return <div className='mt-6'><ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} /></div>;
    }
    // custom range picked but not yet complete — show only the filter bar
    if (!data) {
        return (
            <div className='mt-6'>
                <FilterBar
                    meta={null}
                    filters={filters}
                    onChange={setFilters}
                    onRefresh={() => setReloadKey((k) => k + 1)}
                    onExport={() => { }}
                    refreshing={loading}
                />
            </div>
        );
    }

    const { meta, summary, comparison, revenue_trend, revenue_split, subscription_cycle_split,
        plan_breakdown, plan_popularity, subscription_health, membership,
        revenue_by_city, revenue_by_source, top_customers, recent_transactions } = data;

    const noSales = summary.total_transactions === 0 && summary.assigned_plans === 0;

    return (
        // keep the old numbers visible and dim them while refetching
        <div className={`mt-6 space-y-4 transition-opacity ${loading ? 'opacity-55' : ''}`}>
            <FilterBar
                meta={meta}
                filters={filters}
                onChange={setFilters}
                onRefresh={() => setReloadKey((k) => k + 1)}
                onExport={() => exportPlansCsv(data)}
                refreshing={loading}
            />

            {error && (
                <p className='text-[11px] font-semibold text-[#ef4444]'>
                    Refresh failed ({error}) — showing the last successful load.
                </p>
            )}

            <AlertStrip membership={membership} health={subscription_health} />

            <KpiRow summary={summary} comparison={comparison} />

            {noSales ? (
                <div className='bg-white rounded-lg shadow-md'>
                    <EmptyState
                        title='No plan sales in this period'
                        body='Try a wider range — subscription and membership figures below are current regardless.'
                        action={
                            <button
                                type='button'
                                onClick={() => setFilters({ ...filters, filterType: 'all_time' })}
                                className='px-4 py-1.5 text-xs font-medium text-white bg-[#0F66C8] rounded-md'
                            >
                                View all time
                            </button>
                        }
                    />
                </div>
            ) : (
                <>
                    <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
                        <div className='lg:col-span-2'>
                            <RevenueTrend trend={revenue_trend} summary={summary} granularity={meta.granularity} />
                        </div>
                        <RevenueMix split={revenue_split} total={summary.total_revenue} summary={summary} />
                    </div>

                    <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
                        <div className='lg:col-span-2'>
                            <PlanPopularity plans={plan_breakdown} />
                        </div>
                        <PopularityCallouts popularity={plan_popularity} />
                    </div>
                </>
            )}

            {/* live snapshots — rendered even when the window has zero sales */}
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
                <SubscriptionFunnel health={subscription_health} />
                <MembershipCard membership={membership} />
                <CycleSplit cycleSplit={subscription_cycle_split} summary={summary} />
            </div>

            <PlansTable plans={plan_breakdown} />

            <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
                <div className='lg:col-span-2'>
                    <TopCustomers rows={top_customers} />
                </div>
                <RevenueByCity cities={revenue_by_city} />
            </div>

            <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
                <div className='lg:col-span-2'>
                    <RecentTransactions rows={recent_transactions} />
                </div>
                <RevenueBySource sources={revenue_by_source} />
            </div>

            <div className='text-[11px] text-gray-400 space-y-0.5 pb-2'>
                {meta.legacy_dated_rows > 0 && (
                    <p><em>{count(meta.legacy_dated_rows)} of these records predate transaction timestamps and are dated by insertion time.</em></p>
                )}
                {meta.notes ? <p>{meta.notes}</p> : null}
                <p>Timezone {meta.timezone} · generated {moment(meta.generated_at).format('DD MMM YY, HH:mm')}</p>
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

const Dashboard = () => {
    const token = localStorage.getItem('token');
    const [deshboardData, setDeshboardData] = useState([]);
    const [filterType, setFilterType] = useState('daily');

    const GetDashboardJobActivity = useCallback(async () => {
        const data = {
            filterType: filterType
        }
        try {
            let result = await adminGetDashboard(data, token);
            setDeshboardData(result?.data?.data)
        } catch (error) {
            console.log(error)
        }
    }, [filterType, token])
    useEffect(() => {
        GetDashboardJobActivity();
    }, [filterType, token, GetDashboardJobActivity])

    const topCards = useMemo(() => ([
        { label: 'Total Users', value: deshboardData?.customer_count },
        { label: 'Total Vendors', value: deshboardData?.vendor_count },
        { label: 'User Bookings', value: deshboardData?.user_approved_trip_count },
        { label: 'Vendor Bookings', value: deshboardData?.vendor_approved_trip_count },
    ]), [deshboardData])

    return (
        <div className='bg-gray-100 min-h-[calc(100vh-65px)] h-fit py-5 px-5 sm:px-10'>
            <div className='w-full block md:flex justify-between'>
                <div>
                    <p className='text-lg text-gray-600 font-medium'>Welcome to Urban Cab Portal </p>
                    <p className='text-sm text-[#0F66C8]'> Track Your Trip Activity, Bookings & Trips here.</p>
                </div>
                <div>
                    <select
                        className='w-60 h-8 mt-3 px-2 text-black bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none cursor-pointer'
                        value={filterType}
                        onChange={(e) => { setFilterType(e.target.value) }}
                    >
                        <option value='daily'>Daily</option>
                        <option value='weekly'>Weekly</option>
                        <option value='monthly'>Monthly</option>
                        <option value='yearly'>Yearly</option>
                        <option value='all_time'>All Time</option>
                    </select>
                </div>
            </div>

            {/* compact activity cards */}
            <div className='grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5'>
                {topCards.map((c) => (
                    <div key={c.label} className='bg-white px-3 py-2.5 rounded-lg shadow-md text-center'>
                        <p className='text-[11px] font-medium text-gray-400'>{c.label}</p>
                        <CountUp className='text-lg font-semibold text-[#394253]' end={c.value || 0} duration={3} />
                    </div>
                ))}
            </div>

            <PlanAnalytics />
        </div>
    )
}

export default Dashboard
